import { YesObject } from '@yes.technology/react-toolkit'
import useSitemodelState from '../useSitemodelState/useSitemodelState'
import {
  ActionButtonEnum,
  ActionButtonProps
} from 'component/GenericObjectCard/GenericObjectCard.types'
import { useCallback } from 'react'
import { useLocales } from 'locales/hooks'
import { useFeatureFlag } from 'featureFlags/useFeatureFlag'

interface SitemodelContent {
  action_buttons?: ActionButtonProps[]
}

const getRedirectUrl = (
  actionButton: ActionButtonProps,
  executorSitemodelUuid: string,
  isProcessmodel?: boolean
): string => {
  switch (actionButton.type) {
    case ActionButtonEnum.default:
    case ActionButtonEnum.helptext:
      return `/${actionButton.action}`

    case ActionButtonEnum.link:
      return actionButton.action

    case ActionButtonEnum.workflowmodel:
      return isProcessmodel
        ? `/${executorSitemodelUuid}?processmodelUuid=${actionButton.action}`
        : `/${executorSitemodelUuid}?workflowmodelUuid=${actionButton.action}`
    default:
      return '#'
  }
}

const handleRedirect = (
  button: ActionButtonProps,
  executorSitemodelUuid: string,
  isProcessmodel?: boolean
) => {
  const redirectUrl = getRedirectUrl(
    button,
    executorSitemodelUuid,
    isProcessmodel
  )
  globalThis.location.href = redirectUrl
}

const useRedirect = () => {
  const { sitemodelState } = useSitemodelState()

  const { getSitemodelByLocale } = useLocales()
  const workflowmodelExecutorUuid = getSitemodelByLocale({
    demand: 'workflowmodelExecutorUuid'
  })

  const processmodelExecutorUuid = getSitemodelByLocale({
    demand: 'processmodelExecutorUuid'
  })

  const [isProcessmodelEnabled] = useFeatureFlag('processmodels')

  const stateWithOneObject = useCallback(
    (identifier: string, onFinish?: () => void) => {
      const state = sitemodelState?.[identifier] as YesObject[]

      if (!Array.isArray(state)) {
        // The filteraction execution is not finished yet
        return
      }

      if (state.length !== 1) {
        onFinish?.()
        return
      }

      const sitemodelContent = state?.[0]?.sitemodel_content

      const content: SitemodelContent =
        typeof sitemodelContent === 'string' ? JSON.parse(sitemodelContent) : {}

      const actionButtons = content?.action_buttons

      if (actionButtons && actionButtons.length === 1) {
        const actionButton = actionButtons[0]

        if (actionButton.type === ActionButtonEnum.oneclick) {
          onFinish?.()
          return
        }

        handleRedirect(
          actionButton,
          isProcessmodelEnabled
            ? processmodelExecutorUuid
            : workflowmodelExecutorUuid,
          isProcessmodelEnabled
        )
        return
      }

      onFinish?.()
    },
    [
      sitemodelState,
      workflowmodelExecutorUuid,
      processmodelExecutorUuid,
      isProcessmodelEnabled
    ]
  )

  return {
    stateWithOneObject
  }
}

export default useRedirect
